import React, { useState, useEffect, useRef, useCallback } from "react";
import _ from "lodash";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import NumberFormat from "react-number-format";

import { getDisplayNameFromKey, shiftObjectProps, getUpdatedChartConfigData } from "../utils";
import {
    CHART_DATA_VIEWS,
    CHART_ITEMS,
    INDV_BUILDING_EXPORT_ENTITIES,
    CHART_ITEMS_FOR_EXCEL,
    EXPORTING_CHART_SECTIONS,
    DEFAULT_PROPERTY_VALUE
} from "../constants";
import { reorderArray, showAlert as showAlertUtil } from "../../../config/utils";
import viewIcon from "../../../assets/img/Group 138.svg";
import RightArrowIcon from "../../../assets/img/arrow-right-btn.svg";
import ViewExportModal from "../../recommendations/components/ExportSettingsModal";
import Portal from "../../common/components/Portal";
import { recommendationsTableData } from "../../../config/tableData";
import OuterDraggable from "react-draggable";
import reportAction from "../../reportProperties/actions";
import actions from "../actions";
import { useDispatch, useSelector } from "react-redux";
import TileCustomHeading from "./TileCustomHeading";
import { SketchPicker } from "react-color";
import reactCSS from "reactcss";
import Loader from "../../common/components/Loader";

const ChartItemConfigModal = ({
    setSmartChartData,
    configData = {},
    onCancel,
    defaultData = {},
    configOrder = {},
    isView = false,
    isExcelExport = false,
    isSkeletonView,
    chartData,
    selectedClient,
    setSettingsChange,
    setCustomDataForSave
}) => {
    const dispatch = useDispatch();
    const [chartConfigData, setChartConfigData] = useState(getUpdatedChartConfigData({ ...configData?.selectedChartConfig }));
    const [chartViews, setChartViews] = useState([]);
    const [isIndividualExport, setIsIndividualExport] = useState(
        (configData?.chartKey === "sorted_recom"
            ? configData?.selectedChartConfig?.band1?.is_indv_bld
            : configData?.selectedChartConfig?.is_indv_bld) || false
    );
    const [sortType, setSortType] = useState(configData?.selectedChartConfig?.band1?.type?.[0] || "building");
    const [systemTopCombination, setSystemTopCombination] = useState(configData?.selectedChartConfig?.nslice || null);
    const [isIndividualTradeExport, setIsIndividualTradeExport] = useState(
        configData?.selectedChartConfig?.hasOwnProperty("combination") ? !configData?.selectedChartConfig?.combination : true
    );
    const [defChartView, setDefChartView] = useState([]);
    const [tablePageBreak, setTablePageBreak] = useState(configData?.selectedChartConfig?.remove_tbl_section_break || false);
    const [summaryPageBreak, setSummaryPageBreak] = useState(configData?.selectedChartConfig?.summary_view?.remove_section_break || false);
    const [detailPageBreak, setDetailPageBreak] = useState(configData?.selectedChartConfig?.detailed_view?.remove_section_break || false);
    const [recomendationPageBreak, setRecomendationPageBreak] = useState(
        configData?.selectedChartConfig?.normal_recom?.band1?.remove_section_break || false
    );
    const [showMore, setShowMore] = useState("");
    const [modalDraggable, setModalDraggable] = useState(false);
    const [tiles, setTiles] = useState({
        summary_view: { width: "", height: "", autoCalculate: true },
        detailed_view: { width: "", height: "", autoCalculate: true }
    });
    const [focusedInput, setFocusedInput] = useState(null);
    const [errorMessage, setErrorMessage] = useState({
        summary_view: { message: "", width: null, height: null },
        detailed_view: { message: "", width: null, height: null }
    });
    const [showViewModalExport, setShowViewModalExport] = useState(false);
    const tableData = {
        keys: _.cloneDeep(recommendationsTableData.keys),
        config: _.cloneDeep(recommendationsTableData.config)
    };
    const [transform, setTransform] = useState({ left: 0, top: 0 });
    const [heading, setHeading] = useState({ table_view: false, detailed_view: false, summary_view: false });
    const [isCustomTypeHeading, setIsCustomTypeHeading] = useState({ table_view: false, detailed_view: false, summary_view: false });
    const [colorModalState, setColorModalState] = useState("");
    const defaultHeadingStyle = {
        name: "",
        bold: false,
        color: "ffffff",
        font_size: 10,
        font_id: "",
        bg_color: "003f2d",
        alignment: "left",
        cell_margin: { top: 0.04, left: 0.04, right: 0.04, bottom: 0.04 },
        heading_style: "Heading 3"
    };
    const colorPickerRef = useRef(null);
    const { getCAbandDropdownData } = useSelector(state => state.smartChartReducer);
    const initialViews = ["table_view", "detailed_view", "summary_view"];
    const [headingStyleData, setHeadingStyleData] = useState({
        ...Object.fromEntries(initialViews.map(view => [view, { ...defaultHeadingStyle }])),
        ...((configData?.chartKey?.includes("system") || configData?.chartKey?.includes("multisystem_")) && {
            normal_recom: { ...defaultHeadingStyle }
        })
    });
    const [error, setError] = useState({ table_view: false, summary_view: false, detailed_view: false });
    const [showDropdown, setShowDropdown] = useState("");
    const [filterData, setFilterData] = useState({});
    const [filterDataName, setFilterDataName] = useState({});
    const [filterSearch, setFilterSearch] = useState("");
    const [tempFilterData, setTempFilterData] = useState({});
    const [tempFilterDataName, setTempFilterDataName] = useState({});
    const [isSoftCost, setIsSoftCost] = useState(configData?.selectedChartConfig?.band1?.is_soft_cost ?? false);
    const [energyChartType, setEnergyChartType] = useState([]);

    const validSortKeys = ["name", "value"];
    const validSortDirections = ["asc", "desc"];

    const getInitialSortOrder = view => {
        const sortOrder = configData?.selectedChartConfig?.[view]?.config?.sort_by;
        if (sortOrder && typeof sortOrder === "object") {
            const key = Object.keys(sortOrder)[0] || null;
            const direction = key ? sortOrder[key] : null;
            if (validSortKeys.includes(key) && validSortDirections.includes(direction)) {
                return { key, direction };
            }
        }
        return { key: null, direction: null };
    };

    const [sortOrder, setSortOrder] = useState({
        summary_view: getInitialSortOrder("summary_view"),
        detailed_view: getInitialSortOrder("detailed_view")
    });

    const getSortOrderConfigValue = currentSort => (currentSort?.key && currentSort?.direction ? { [currentSort.key]: currentSort.direction } : null);

    const existingKeys = ["em_eci_site_cost", "em_eci_energy_cost", "bld_brkdown_energy_cost"];
    const chartLabels = {
        em_eci_site_cost: "Site Breakdown Energy Cost Index ($ per SF)",
        em_eci_energy_cost: "Annual Average Energy Unit Cost Analysis ($ per MMBTU)",
        bld_brkdown_energy_cost: "Annual Average Building Breakdown Total Energy Cost ($)"
    };
    const hasTableConfigExport = (configData?.chartKey === "buildings" || configData?.chartKey === "additions") && isExcelExport;
    const isSortedRecommendation = configData?.chartKey === "sorted_recom";

    const only2DKeys = [
        "em_eui_building_breakdown",
        "em_eci_building_breakdown",
        "em_eui_site_brkdown_energy_use",
        "em_eci_site_cost",
        "monthly_electricity_cost",
        "monthly_gas_cost",
        "monthly_total_energy_cost",
        "monthly_electricity_usage",
        "monthly_gas_usage",
        "monthly_total_energy_usage",
        "bld_brkdown_energy_usage",
        "em_eui_energy_usage",
        "bld_brkdown_energy_cost",
        "em_eci_site_cost"
    ];

    useEffect(() => {
        dispatch(reportAction?.getDropdownList("fonts"));
        let currentChartItems = isExcelExport ? CHART_ITEMS_FOR_EXCEL : CHART_ITEMS;
        let defaultChartView = currentChartItems?.[configData.entity]?.[
            configData?.chartKey?.includes("multisystem_") ? "system" : configData?.chartKey
        ]?.config
            ? Object.keys(
                  currentChartItems[configData.entity][configData?.chartKey?.includes("multisystem_") ? "system" : configData?.chartKey].config
              )
            : [];
        const constantConfigKeys =
            currentChartItems?.project[configData?.chartKey?.includes("multisystem_") ? "system" : configData?.chartKey]?.config &&
            Object.keys(currentChartItems?.project[configData?.chartKey?.includes("multisystem_") ? "system" : configData?.chartKey]?.config);
        const providedConfigKeysInOrder = [];

        Object.keys(configData?.selectedChartConfig).forEach(key => {
            if (constantConfigKeys?.includes(key)) {
                providedConfigKeysInOrder?.push(key);
            }
        });
        constantConfigKeys?.forEach(key => {
            if (!providedConfigKeysInOrder?.includes(key)) {
                providedConfigKeysInOrder?.push(key);
            }
        });
        let currentChartView = configOrder?.[configData.chartKey]
            ? [...configOrder?.[configData.chartKey]]
            : providedConfigKeysInOrder?.length > 0
            ? providedConfigKeysInOrder
            : [];
        setChartViews(currentChartView?.length ? [...currentChartView] : defaultChartView);
        setShowMore(currentChartView?.length ? currentChartView && currentChartView[0] : defaultChartView[0]);
        setDefChartView(defaultChartView);
        if ((configData?.chartKey?.includes("multisystem_") || configData?.chartKey?.includes("system")) && chartConfigData?.normal_recom) {
            setChartConfigData(prevConfig => {
                return {
                    ...prevConfig,
                    normal_recom: {
                        ...prevConfig["normal_recom"],
                        band1: {
                            ...prevConfig["normal_recom"]?.band1,
                            mfilter: {
                                ...prevConfig["normal_recom"]?.band1?.mfilter,
                                ...chartData?.properties_text?.project?.band1?.mfilter
                            }
                        }
                    }
                };
            });
        }
        if (configData?.chartKey === "energy_band") {
            const selectedChartConfig = configData?.selectedChartConfig || {};

            const trueKeys = Object.keys(selectedChartConfig).filter(key => selectedChartConfig[key] === true);

            setEnergyChartType(trueKeys);
        }
    }, []);
    useEffect(() => {
        if (!chartConfigData) return;
        const { summary_view, detailed_view, table_heading, normal_recom, trade_ids = [], system_ids = [], config_filter_names } = chartConfigData;

        if (summary_view?.config || detailed_view?.config) {
            setTiles(prevTiles => ({
                ...prevTiles,
                summary_view: {
                    ...prevTiles.summary_view,
                    width: summary_view?.config?.width,
                    height: summary_view?.config?.height,
                    autoCalculate: summary_view?.config?.autoCalculate ?? true
                },
                detailed_view: {
                    ...prevTiles.detailed_view,
                    width: detailed_view?.config?.width,
                    height: detailed_view?.config?.height,
                    autoCalculate: detailed_view?.config?.autoCalculate ?? true
                }
            }));

            ["summary_view", "detailed_view"].forEach(view => {
                const viewConfig = chartConfigData?.[view]?.config?.heading;
                if (viewConfig) processHeadingData(view, viewConfig);
            });
        }
        if (normal_recom?.band1?.custom_heading_props || normal_recom?.band1?.structured_heading)
            processHeadingData("normal_recom", chartConfigData?.["normal_recom"]?.band1);
        if (table_heading) processHeadingData("table_view", table_heading);

        if (trade_ids) {
            filterData["trade"] = trade_ids;
            filterDataName["trade"] = config_filter_names?.CA_BandFilter?.Trade || [];
        }
        if (system_ids) {
            filterData["system"] = system_ids;
            filterDataName["system"] = config_filter_names?.CA_BandFilter?.System || [];
        }
    }, []);

    const processHeadingData = (view, viewConfig) => {
        setHeading(prev => ({ ...prev, [view]: true }));
        setHeadingStyleData(prev => ({
            ...prev,
            [view]: { ...prev?.[view], name: viewConfig?.name }
        }));
        if ("custom_heading_props" in viewConfig) {
            setIsCustomTypeHeading(prev => ({ ...prev, [view]: true }));
            setHeadingStyleData(prev => ({
                ...prev,
                [view]: { ...prev?.[view], ...viewConfig.custom_heading_props }
            }));
        } else {
            setHeadingStyleData(prev => ({
                ...prev,
                [view]: { ...prev?.[view], ...viewConfig.structured_heading }
            }));
        }
    };

    const showAlert = view => showAlertUtil(errorMessage[view]?.message);
    const handleChartConfigData = (action, chartView, params) => {
        const { is_remove = false, chartType, exportChartSectionType = "", key = "", value = "" } = params;
        switch (action) {
            case "view":
                if (is_remove) {
                    setChartConfigData(prevConfig => {
                        return {
                            ..._.omit(prevConfig, [chartView])
                        };
                    });
                    if (chartView === "summary_view" || chartView === "detailed_view") {
                        setSortOrder(prev => ({ ...prev, [chartView]: { key: null, direction: null } }));
                    }
                } else {
                    if (chartView === "table_view") {
                        setChartConfigData(prevConfig => {
                            return {
                                ...prevConfig,
                                [chartView]: ""
                            };
                        });
                    } else if (chartView === "normal_recom") {
                        setChartConfigData(prevConfig => {
                            return {
                                ...prevConfig,
                                [chartView]: {
                                    band1: {
                                        ...configData?.selectedChartConfig["normal_recom"]?.band1
                                        // ...chartData?.properties_text?.project?.band1?.mfilter
                                    }
                                }
                            };
                        });
                    } else {
                        const { entity, chartKey } = configData;
                        const key = chartKey?.startsWith("multisystem") ? "system" : chartKey;
                        let previousSelectedValue =
                            defaultData?.[entity]?.band1?.type?.[key]?.[chartView]?.chart_type ||
                            DEFAULT_PROPERTY_VALUE?.[entity]?.band1?.type?.[key]?.[chartView]?.chart_type ||
                            [];
                        let previousFundingOptions =
                            defaultData?.[entity]?.band1?.type?.[key]?.[chartView]?.config?.funding_source ||
                            DEFAULT_PROPERTY_VALUE?.[entity]?.band1?.type?.[key]?.[chartView]?.config?.funding_source ||
                            [];
                        setChartConfigData(prevConfig => {
                            return {
                                ...prevConfig,
                                [chartView]: {
                                    config: prevConfig[chartView]?.config || {
                                        legend: true,
                                        color_legend: true,
                                        header: true,
                                        total: true,
                                        width: null,
                                        height: null,
                                        autoCalculate: true,
                                        funding_source: [...previousFundingOptions]
                                    },
                                    chart_type: [...previousSelectedValue],
                                    remove_section_break: prevConfig[chartView]?.remove_section_break || false
                                }
                            };
                        });
                    }
                }

                break;
            case "chartType":
                let currentTypes = chartConfigData[chartView]?.chart_type ? [...chartConfigData[chartView].chart_type] : [];
                let updatedChartTypes = [];

                if (is_remove) {
                    updatedChartTypes = currentTypes.filter(type => type !== chartType);
                } else {
                    updatedChartTypes = [...currentTypes, chartType];
                }
                setChartConfigData(prevConfig => {
                    return {
                        ...prevConfig,
                        [chartView]: {
                            ...prevConfig[chartView],
                            chart_type: [...updatedChartTypes]
                        }
                    };
                });
                break;
            case "fundingOption":
                let currentFundingOptions = chartConfigData[chartView]?.config?.funding_source
                    ? [...chartConfigData[chartView]?.config.funding_source]
                    : [];
                let updatedFundingOptions = [];

                if (is_remove) {
                    updatedFundingOptions = currentFundingOptions.filter(type => type !== chartType);
                } else {
                    updatedFundingOptions = [...currentFundingOptions, chartType];
                }
                setChartConfigData(prevConfig => {
                    return {
                        ...prevConfig,
                        [chartView]: {
                            ...prevConfig[chartView],
                            config: {
                                ...chartConfigData[chartView]?.config,
                                funding_source: [...updatedFundingOptions]
                            }
                        }
                    };
                });
                break;
            case "allSelect":
                if (is_remove) {
                    setChartConfigData({
                        ...(chartConfigData || {}),
                        summary_view: {
                            ...(chartConfigData?.summary_view || {}),
                            chart_type: [],
                            config: chartConfigData?.summary_view?.config || { legend: true, color_legend: true, header: true, total: true }
                        },
                        detailed_view: {
                            ...(chartConfigData?.detailed_view || {}),
                            chart_type: [],
                            config: { ...chartConfigData?.detailed_view?.config, funding_source: [] } || {
                                legend: true,
                                color_legend: true,
                                header: true,
                                total: true,
                                funding_source: []
                            }
                        }
                    });
                } else {
                    let allChartTypes = {};
                    if (configData?.chartKey === "EFCI") {
                        allChartTypes = {
                            detailed_view: {
                                chart_type: ["line_column_2d"],
                                config: {
                                    legend: true,
                                    color_legend: true,
                                    header: true,
                                    total: true,
                                    width: null,
                                    height: null,
                                    autoCalculate: true,
                                    funding_source: [1, 2, 3, 4]
                                }
                            }
                        };
                    } else {
                        allChartTypes = {
                            ...(chartConfigData || {}),
                            summary_view: {
                                ...(chartConfigData?.summary_view || {}),
                                chart_type:
                                    configData?.chartKey === "building_addition_sf"
                                        ? ["donut_2d", "donut_3d"]
                                        : configData?.chartKey === "em_eci_energy_cost" || configData?.chartKey === "project_cost"
                                        ? ["pie_2d", "pie_3d"]
                                        : only2DKeys.includes(configData?.chartKey)
                                        ? ["pie_2d"]
                                        : ["pie_2d", "pie_3d", "donut_2d", "donut_3d"],
                                config: chartConfigData?.summary_view?.config || { legend: true, color_legend: true, header: true, total: true }
                            },
                            detailed_view: {
                                ...(chartConfigData?.detailed_view || {}),
                                chart_type: only2DKeys.includes(configData?.chartKey)
                                    ? ["stacked_column_2d"]
                                    : ["stacked_column_2d", "stacked_column_3d"],
                                config: chartConfigData?.detailed_view?.config || { legend: true, color_legend: true, header: true, total: true }
                            },
                            table_view: ""
                        };
                    }
                    setChartConfigData(prevConfig => {
                        return {
                            ...allChartTypes
                        };
                    });
                }
                break;
            case "exportChartSection":
                let currentExportChartSectionTypes = chartConfigData[chartView]?.config || {
                    legend: true,
                    color_legend: true,
                    header: true,
                    total: true
                };
                setChartConfigData(prevConfig => {
                    return {
                        ...prevConfig,
                        [chartView]: {
                            ...prevConfig?.[chartView],
                            config: {
                                ...currentExportChartSectionTypes,
                                [exportChartSectionType]: !is_remove
                            }
                        }
                    };
                });
                break;
            case "exportChartSectionOthersColor":
                setChartConfigData(prevConfig => {
                    return {
                        ...prevConfig,
                        [chartView]: {
                            ...prevConfig?.[chartView],
                            config: {
                                ...prevConfig?.[chartView]?.config,
                                [key]: value
                            }
                        }
                    };
                });
                break;
            case "pageBreak":
                const { pageBreak } = params;
                if (chartView === "summary_view") {
                    setChartConfigData(prevConfig => {
                        return {
                            ...prevConfig,
                            [chartView]: {
                                ...prevConfig?.[chartView],
                                remove_section_break: pageBreak
                            }
                        };
                    });
                }
                if (chartView === "detailed_view") {
                    setChartConfigData(prevConfig => {
                        return {
                            ...prevConfig,
                            [chartView]: {
                                ...prevConfig?.[chartView],
                                remove_section_break: pageBreak
                            }
                        };
                    });
                }
                if (chartView === "normal_recom") {
                    setChartConfigData(prevConfig => {
                        return {
                            ...prevConfig,
                            [chartView]: {
                                band1: {
                                    ...prevConfig?.[chartView]?.band1,
                                    remove_section_break: pageBreak
                                }
                            }
                        };
                    });
                }
                break;
            case "showColumns": {
                const { column, field, checked } = params;
                setChartConfigData(prev => {
                    const prevColumns = prev?.show_columns || {};

                    return {
                        ...prev,
                        show_columns: column
                            ? {
                                  ...prevColumns,
                                  [column]: {
                                      ...(prevColumns?.[column] || {}),
                                      [field]: checked
                                  }
                              }
                            : {
                                  ...prevColumns,
                                  [field]: checked
                              }
                    };
                });

                break;
            }
            default:
        }
    };
    useEffect(() => {
        if (errorMessage?.detailed_view?.message || errorMessage?.summary_view?.message) {
            if (errorMessage?.summary_view?.message) {
                showAlert("summary_view");
            } else if (errorMessage?.detailed_view?.message) {
                showAlert("detailed_view");
            }
        }
    }, [errorMessage]);

    const handleSortOrder = (view, sortKey) => {
        if (isView || isSkeletonView) return;

        setSortOrder(prev => {
            const current = prev[view] || { key: null, direction: null };

            let nextSort;
            if (current.key !== sortKey) {
                nextSort = { key: sortKey, direction: "asc" };
            } else {
                nextSort = { key: sortKey, direction: current.direction === "asc" ? "desc" : "asc" };
            }

            updateSortOrderConfig(view, getSortOrderConfigValue(nextSort));

            return {
                ...prev,
                [view]: nextSort
            };
        });
    };

    const updateSortOrderConfig = (chartView, sortOrderValue) => {
        setChartConfigData(prevConfig => {
            const configWithoutSortOrder = _.omit(prevConfig?.[chartView]?.config || {}, ["sort_by"]);

            return {
                ...prevConfig,
                [chartView]: {
                    ...prevConfig?.[chartView],
                    config: {
                        ...configWithoutSortOrder,
                        ...(sortOrderValue ? { sort_by: sortOrderValue } : {})
                    }
                }
            };
        });
    };

    const renderSortOrderButtons = view => {
        if (view !== "summary_view" && view !== "detailed_view") return null;
        if (!chartConfigData?.[view]?.hasOwnProperty("chart_type")) return null;

        const currentSort = sortOrder[view] || { key: null, direction: null };

        return (
            <div className="fot-select">
                <label>Sort Order</label>
                <div className="btn-grp smart-chart-sort-btn-group align-items-center d-flex mb-3">
                    <button
                        type="button"
                        className={`btn smart-chart-sort-btn ml-1${currentSort.key === "name" ? " act-btn" : ""}`}
                        onClick={() => handleSortOrder(view, "name")}
                        disabled={isView || isSkeletonView}
                        data-tip="Sort Chart By Name"
                        data-effect="solid"
                        data-place="left"
                        data-background-color="#007bff"
                        data-delay-show="500"
                    >
                        Name
                        {currentSort.key === "name" && currentSort.direction === "asc" ? (
                            <i className="fas fa-long-arrow-alt-up table-param-rep text-danger ml-1" />
                        ) : currentSort.key === "name" && currentSort.direction === "desc" ? (
                            <i className="fas fa-long-arrow-alt-down table-param-rep text-danger ml-1" />
                        ) : null}
                    </button>
                    <button
                        type="button"
                        className={`btn smart-chart-sort-btn ml-1 mr-1${currentSort.key === "value" ? " act-btn" : ""}`}
                        onClick={() => handleSortOrder(view, "value")}
                        disabled={isView || isSkeletonView}
                        data-tip="Sort Chart By Value"
                        data-effect="solid"
                        data-place="left"
                        data-background-color="#007bff"
                        data-delay-show="500"
                    >
                        Value
                        {currentSort.key === "value" && currentSort.direction === "asc" ? (
                            <i className="fas fa-long-arrow-alt-up table-param-rep text-danger ml-1" />
                        ) : currentSort.key === "value" && currentSort.direction === "desc" ? (
                            <i className="fas fa-long-arrow-alt-down table-param-rep text-danger ml-1" />
                        ) : null}
                    </button>
                </div>
            </div>
        );
    };

    const prepareChartConfig = () => {
        setErrorMessage({ summary_view: { message: "", width: null, height: null }, detailed_view: { message: "", width: null, height: null } });

        const selectedTile = configData?.selectedChartConfig?.band1 || configData?.selectedChartConfig || {};
        let additionalConfigData = {
            ...(selectedTile?.hasOwnProperty("name") ? { name: selectedTile?.name } : {}),
            ...(selectedTile?.hasOwnProperty("custom_heading_props") ? { custom_heading_props: selectedTile?.custom_heading_props } : {}),
            ...(selectedTile?.hasOwnProperty("structured_heading") ? { structured_heading: selectedTile?.structured_heading } : {}),
            ...(INDV_BUILDING_EXPORT_ENTITIES.includes(configData?.chartKey?.includes("multisystem_") ? "system" : configData?.chartKey) &&
            isIndividualExport
                ? { is_indv_bld: "T" }
                : {})
        };
        let chartData = CHART_DATA_VIEWS;
        let chartViewVlaue = chartViews;
        if (isSortedRecommendation) {
            additionalConfigData = {
                ...additionalConfigData,
                type: [sortType],
                is_soft_cost: isSoftCost,
                ...(chartConfigData?.band1?.settings_id ? { settings_id: chartConfigData?.band1?.settings_id } : {})
            };
        }
        if (configData?.chartKey?.includes("multisystem_") || configData?.chartKey === "system") {
            additionalConfigData = { ...additionalConfigData, combination: !isIndividualTradeExport };
            if (systemTopCombination) {
                additionalConfigData = { ...additionalConfigData, nslice: systemTopCombination };
            }
            if (filterData["system"]?.length) {
                additionalConfigData = {
                    ...additionalConfigData,
                    system_ids: filterData["system"],
                    config_filter_names: {
                        ...additionalConfigData.config_filter_names,
                        CA_BandFilter: {
                            ...(additionalConfigData.config_filter_names && additionalConfigData.config_filter_names.CA_BandFilter),
                            System: filterDataName["system"]
                        }
                    }
                };
            }
            if (filterData["trade"]?.length) {
                additionalConfigData = {
                    ...additionalConfigData,
                    trade_ids: filterData["trade"],
                    config_filter_names: {
                        ...additionalConfigData.config_filter_names,
                        CA_BandFilter: {
                            ...(additionalConfigData.config_filter_names && additionalConfigData.config_filter_names.CA_BandFilter),
                            Trade: filterDataName["trade"]
                        }
                    }
                };
            }
            chartData = [...chartData, "system_ids", "trade_ids"];
            chartViewVlaue = [...chartViews, "system_ids", "trade_ids"];
        }
        if (configData?.chartKey === "subsystem") {
            chartData = [...chartData, "system_ids", "trade_ids", "subsystem_ids"];
            chartViewVlaue = [...chartViews, "system_ids", "trade_ids", "subsystem_ids"];
        }
        const summaryValid = isValidWidthHeight(chartConfigData?.summary_view?.config, "summary_view");
        let detailValid = false;
        if (summaryValid) {
            detailValid = isValidWidthHeight(chartConfigData?.detailed_view?.config, "detailed_view");
        }
        const views = ["summary_view", "detailed_view", "table_view", "normal_recom"];
        let savedConfigData = { ...chartConfigData };
        let headingStyleValidation = { table_view: false, summary_view: false, detailed_view: false };
        if (hasTableConfigExport) {
            additionalConfigData = {
                ...additionalConfigData,
                ...(savedConfigData?.settings_id ? { settings_id: savedConfigData?.settings_id ?? "" } : {})
            };
        }
        if (configData?.chartKey === "project_cost") {
            additionalConfigData = {
                ...additionalConfigData,
                show_columns: {
                    setting: savedConfigData?.show_columns?.setting ?? false,
                    actual: savedConfigData?.show_columns?.actual ?? false,
                    Setting: {
                        percentage: chartConfigData?.show_columns?.setting && savedConfigData?.show_columns?.Setting?.percentage,
                        value: chartConfigData?.show_columns?.setting && savedConfigData?.show_columns?.Setting?.value
                    },
                    Actual: {
                        percentage: chartConfigData?.show_columns?.actual && savedConfigData?.show_columns?.Actual?.percentage,
                        value: chartConfigData?.show_columns?.actual && savedConfigData?.show_columns?.Actual?.value
                    }
                }
            };
        }
        views.forEach(view => {
            if (Object.keys(chartConfigData || {}).includes(view) && heading[view]) {
                const headingData = {
                    name: headingStyleData?.[view]?.name,
                    ...(isCustomTypeHeading?.[view]
                        ? {
                              custom_heading_props: Object.fromEntries(
                                  Object.entries(headingStyleData?.[view] || {}).filter(([key]) => key !== "name" && key !== "heading_style")
                              )
                          }
                        : {
                              structured_heading: {
                                  heading_style: headingStyleData?.[view]?.heading_style
                              }
                          })
                };
                if (!headingStyleData?.[view]?.name?.trim()) {
                    headingStyleValidation = { ...headingStyleValidation, [view]: true };
                } else {
                    headingStyleValidation = { ...headingStyleValidation, [view]: false };
                }

                if (view === "table_view") {
                    chartData = [...chartData, "table_heading"];
                    chartViewVlaue = [...chartViewVlaue, "table_heading"];
                    savedConfigData = {
                        ...savedConfigData,
                        table_heading: headingData
                    };
                } else if (view === "normal_recom") {
                    savedConfigData[view] = {
                        ...savedConfigData[view],
                        band1: {
                            ...savedConfigData?.[view]?.band1,
                            ...headingData
                        }
                    };
                } else {
                    savedConfigData[view] = {
                        ...savedConfigData[view],
                        config: {
                            ...savedConfigData[view]?.config,
                            heading: headingData
                        }
                    };
                }
            } else {
                if (view === "table_view") {
                    delete savedConfigData.table_heading;
                } else {
                    if (savedConfigData[view]?.config?.heading) {
                        delete savedConfigData[view].config.heading;
                    }
                }
            }
        });
        ["summary_view", "detailed_view"].forEach(view => {
            if (!savedConfigData?.[view]?.config) return;

            const configWithoutSortOrder = _.omit(savedConfigData[view].config, ["sort_by"]);
            const sortOrderConfigValue = getSortOrderConfigValue(sortOrder[view]);

            savedConfigData[view] = {
                ...savedConfigData[view],
                config: {
                    ...configWithoutSortOrder,
                    ...(sortOrderConfigValue ? { sort_by: sortOrderConfigValue } : {})
                }
            };
        });
        return {
            savedConfigData,
            chartData,
            chartViewVlaue,
            additionalConfigData,
            summaryValid,
            detailValid,
            headingStyleValidation
        };
    };
    const handleClickCancel = useCallback(() => {
        const { savedConfigData, chartData, chartViewVlaue, additionalConfigData } = prepareChartConfig();

        let resultData = {};
        const chartKey = configData?.chartKey || "";
        if (chartKey === "energy_band") {
            resultData = {
                remove_section_break: false,
                ...configData?.energy_band,
                ...(energyChartType?.length
                    ? existingKeys?.reduce((acc, key) => {
                          acc[key] = energyChartType.includes(key);
                          return acc;
                      }, {})
                    : {})
            };
        } else if (chartKey === "sorted_recom") {
            resultData = {
                band1: {
                    mfilter: savedConfigData?.band1?.mfilter || {},
                    ...additionalConfigData
                }
            };
        } else if (
            chartKey.includes("multi_recommendation") ||
            chartKey.includes("normal_recom") ||
            chartKey.includes("recom_onepager") ||
            chartKey.includes("asset-onepager") ||
            chartKey.includes("memo_field") ||
            chartKey.includes("asset_band")
        ) {
            resultData = {
                band1: {
                    mfilter: {},
                    ...shiftObjectProps(shiftObjectProps(savedConfigData, chartData), chartViewVlaue)
                }
            };
        } else {
            resultData = {
                ...shiftObjectProps(shiftObjectProps(savedConfigData, chartData), chartViewVlaue),
                ...additionalConfigData,
                ...(savedConfigData?.hasOwnProperty("detailed_view") && defChartView?.length === 1
                    ? {}
                    : { remove_tbl_section_break: tablePageBreak })
            };
        }

        const chartKeys = chartViewVlaue ?? [];

        const filteredKeysInOrder = chartKeys?.filter(
            key => configData?.selectedChartConfig?.hasOwnProperty(key) && key !== "system_ids" && key !== "trade_ids"
        );

        const isOrderMatched = _.isEqual(
            _.uniq(filteredKeysInOrder),
            _.uniq(Object.keys(configData?.selectedChartConfig || {})?.filter(k => chartKeys.includes(k) && k !== "system_ids" && k !== "trade_ids"))
        );
        onCancel(!_.isEqual(resultData, configData?.selectedChartConfig) || !isOrderMatched);
    }, [prepareChartConfig, configData?.chartKey, tablePageBreak, onCancel]);

    useEffect(() => {
        const handleKeyDown = e => {
            if (e.key === "Escape" && !showViewModalExport) {
                e.preventDefault();
                e.stopPropagation();
                handleClickCancel();
            }
        };
        window.addEventListener("keydown", handleKeyDown);

        return () => {
            window.removeEventListener("keydown", handleKeyDown);
        };
    }, [showViewModalExport, handleClickCancel]);

    const isValidWidthHeight = (config, view) => {
        if (!config?.width && config?.height) {
            setErrorMessage(prevState => ({ ...prevState, [view]: { message: "Width is required", width: true, height: false } }));
            return false;
        } else if (!config?.height && config?.width) {
            setErrorMessage(prevState => ({ ...prevState, [view]: { message: "Height is required", width: false, height: true } }));
            return false;
        } else if (config?.height || config?.width) {
            if (config.height < 30 && config.width < 30) {
                setErrorMessage(prevState => ({
                    ...prevState,
                    [view]: { message: "Both width and height must be at least 30", width: true, height: true }
                }));
            } else if (config?.height < 30) {
                setErrorMessage(prevState => ({ ...prevState, [view]: { message: "Height must be at least 30", width: false, height: true } }));
            } else if (config?.width < 30) {
                setErrorMessage(prevState => ({ ...prevState, [view]: { message: "Width must be at least 30", width: true, height: false } }));
            } else {
                return true;
            }
        } else {
            setErrorMessage(prevState => ({ ...prevState, [view]: null }));
            return true;
        }
    };
    const handleClickSave = () => {
        const { summaryValid, detailValid, headingStyleValidation, savedConfigData, chartData, chartViewVlaue, additionalConfigData } =
            prepareChartConfig();
        setError({ ...headingStyleValidation });
        if (summaryValid && detailValid && !Object.values(headingStyleValidation)?.some(value => value === true)) {
            setChartConfigData({});
            if (configData?.chartKey === "energy_band") setCustomDataForSave(energyChartType);
            else
                setSmartChartData("config_chart", {
                    ...configData,
                    chartConfigData: { ...shiftObjectProps(savedConfigData, chartData) },
                    configOrder: chartViewVlaue,
                    defaultValue: configData?.selectedChartConfig || null,
                    additionalKeysForConfig: {
                        ...additionalConfigData,
                        ...((savedConfigData?.hasOwnProperty("detailed_view") && defChartView?.length === 1) || isSortedRecommendation
                            ? {}
                            : { remove_tbl_section_break: tablePageBreak })
                    }
                });
            onCancel({}, true);
        }
    };

    const isAllSelected = () => {
        if (configData?.chartKey === "EFCI") {
            if (chartConfigData?.detailed_view?.chart_type?.length === 1) {
                return true;
            }
        } else if (only2DKeys.includes(configData?.chartKey)) {
            if (chartConfigData?.detailed_view?.chart_type?.length === 1 || chartConfigData?.summary_view?.chart_type?.length === 1) {
                return true;
            }
        } else if (configData?.chartKey === "em_eci_energy_cost" || configData?.chartKey === "project_cost") {
            if (chartConfigData?.summary_view?.chart_type?.length === 2) {
                return true;
            }
        } else if (
            configData?.chartKey === "building_addition_sf" &&
            chartConfigData?.summary_view?.chart_type?.length === 2 &&
            chartConfigData?.detailed_view?.chart_type?.length === 2 &&
            chartConfigData.hasOwnProperty("table_view")
        ) {
            return true;
        } else if (
            chartConfigData?.summary_view?.chart_type?.length === 4 &&
            chartConfigData?.detailed_view?.chart_type?.length === 2 &&
            chartConfigData.hasOwnProperty("table_view")
        ) {
            return true;
        }
        return false;
    };

    const renderChartTypes = (view, chartKey) => {
        switch (view) {
            case "summary_view":
                if (chartKey === "building_addition_sf") return ["donut_2d", "donut_3d"];
                if (chartKey === "em_eci_energy_cost" || configData?.chartKey === "project_cost") return ["pie_2d", "pie_3d"];
                if (only2DKeys.includes(chartKey)) return ["pie_2d"];
                return ["pie_2d", "pie_3d", "donut_2d", "donut_3d"];
            case "detailed_view":
                if (chartKey === "EFCI") {
                    return ["line_column_2d"];
                }
                if (only2DKeys.includes(chartKey)) return ["stacked_column_2d"];
                return ["stacked_column_2d", "stacked_column_3d"];
            default:
                return [];
        }
    };

    const onDragEnd = result => {
        if (!result.destination) {
            return;
        }
        const keyList = reorderArray(chartViews, result.source.index, result.destination.index);
        setChartViews(keyList);
    };

    const getIconsForView = view => {
        switch (view) {
            case "summary_view":
                return "sm-charts-pie-chart-icon.svg";
            case "detailed_view":
                return "smart-chart-chart-icon-item.svg";
            case "table_view":
                return "sm-charts-table-blue-icon.svg";
            case "normal_recom":
                return "sm-charts-table-blue-icon.svg";
            default:
                break;
        }
    };
    const aspectRatio = 1.6667;
    function getHeightFromWidth(width) {
        return width / aspectRatio;
    }

    function getWidthFromHeight(height) {
        return height * aspectRatio;
    }
    function handleCheckboxChange(view) {
        setTiles(prevTiles => ({
            ...prevTiles,
            [view]: {
                width: "",
                height: "",
                autoCalculate: !prevTiles[view]?.autoCalculate
            }
        }));
    }

    const handleWidthChange = (value, chartView) => {
        if (focusedInput === "width") {
            setTiles(prevTiles => {
                const updatedTiles = { ...prevTiles };
                // const widthValue = value !== "0" ? value : "";
                const widthValue = value;
                let newHeight = "";
                if (tiles[chartView]?.autoCalculate) {
                    const calculatedHeight = widthValue ? getHeightFromWidth(widthValue) : "";
                    newHeight = calculatedHeight || calculatedHeight === 0 ? calculatedHeight.toFixed(2) : "";
                    updatedTiles[chartView].width = widthValue;
                    updatedTiles[chartView].height = newHeight;
                } else {
                    updatedTiles[chartView].width = value !== "" ? value : "";
                }
                updateChartConfig(chartView, updatedTiles[chartView].width, updatedTiles[chartView].height, updatedTiles[chartView].autoCalculate);
                return updatedTiles;
            });
        }
    };

    const handleHeightChange = (value, chartView) => {
        if (focusedInput === "height") {
            setTiles(prevTiles => {
                const updatedTiles = { ...prevTiles };
                const heightValue = value;
                let newWidth = "";
                if (tiles[chartView]?.autoCalculate) {
                    const calculatedWidth = heightValue ? getWidthFromHeight(heightValue) : "";
                    newWidth = calculatedWidth ? calculatedWidth?.toFixed(2) : "";
                    updatedTiles[chartView].height = heightValue;
                    updatedTiles[chartView].width = newWidth;
                } else {
                    updatedTiles[chartView].height = heightValue;
                }
                updateChartConfig(chartView, updatedTiles[chartView]?.width, updatedTiles[chartView].height, updatedTiles[chartView]?.autoCalculate);
                return updatedTiles;
            });
        }
    };

    const updateChartConfig = (chartView, width, height, autoCalculate) => {
        const numericWidth = !width ? null : parseFloat(width);
        const numericHeight = !height ? null : parseFloat(height);
        if (!isNaN(numericWidth) || !isNaN(numericHeight)) {
            setChartConfigData(prevConfig => ({
                ...prevConfig,
                [chartView]: {
                    ...prevConfig?.[chartView],
                    config: {
                        ...prevConfig?.[chartView]?.config,
                        width: numericWidth,
                        height: numericHeight,
                        autoCalculate
                    }
                }
            }));
        }
    };
    const handleDrag = (e, ui) => {
        const { x, y } = ui;
        setTransform({ left: x, top: y });
    };

    const renderFilteredList = () => {
        let filteredList = [];
        const dropdownData = getCAbandDropdownData?.data || [];
        if (dropdownData?.length) {
            filteredList = filterSearch?.trim()?.length
                ? dropdownData.filter(
                      item =>
                          item.name?.toString()?.toLowerCase()?.includes(filterSearch?.toLocaleLowerCase()) ||
                          item?.trade?.toString()?.toLowerCase()?.includes(filterSearch?.toLocaleLowerCase())
                  )
                : dropdownData;
        }

        return filteredList;
    };

    const handleClickFilterDropdown = (type, elem) => {
        setFilterSearch("");
        if (showDropdown === elem) {
            setShowDropdown("");
        } else {
            setTempFilterData(prev => ({ ...prev, [elem]: filterData[elem] || [] }));
            setTempFilterDataName(prev => ({ ...prev, [elem]: filterDataName[elem] || [] }));
            setShowDropdown(elem);
            dispatch(actions.getCAbandFilterDropDownData(elem, getParamsForMasterFilter(elem)));
        }
    };

    const getParamsForMasterFilter = key => {
        let currentUser = localStorage.getItem("userId") || "";
        const mfilter = chartData.properties_text?.project?.band1?.mfilter;
        const { project_ids, region_ids, site_ids, building_ids, building_type_ids, ...restMfilter } = mfilter;
        switch (key) {
            case "system":
                return {
                    project_id: project_ids?.length ? project_ids : null,
                    region_id: region_ids?.length ? region_ids : null,
                    site_id: site_ids?.length ? site_ids : null,
                    building_id: building_ids?.length ? building_ids : null,
                    building_type_id: building_type_ids?.length ? building_type_ids : null,
                    user_id: currentUser,
                    trade_id: filterData["trade"]?.length ? [...filterData["trade"]] : null,
                    ...restMfilter
                };
            case "trade":
                return {
                    project_id: project_ids?.length ? project_ids : null,
                    region_id: region_ids?.length ? region_ids : null,
                    site_id: site_ids?.length ? site_ids : null,
                    building_id: building_ids?.length ? building_ids : null,
                    building_type_id: building_type_ids?.length ? building_type_ids : null,
                    user_id: currentUser,
                    ...restMfilter
                };
            default:
                return {};
        }
    };

    const handleSelectDropdown = (key, data) => {
        let selectedFilter = tempFilterData[key] ? [...tempFilterData[key]] : [];
        let selectedFilterName = tempFilterDataName[key] ? [...tempFilterDataName[key]] : [];
        if (selectedFilter?.includes(data.id)) {
            selectedFilter = selectedFilter.filter(id => id !== data.id);
            selectedFilterName = selectedFilterName.filter(item => item !== data?.name);
        } else {
            selectedFilter.push(data.id);
            selectedFilterName.push(data?.name);
        }
        setTempFilterData({ ...tempFilterData, [key]: selectedFilter });
        setTempFilterDataName({ ...tempFilterDataName, [key]: selectedFilterName });
    };

    const handleSelectAll = (key, isChecked) => {
        if (isChecked) {
            const allIds = getCAbandDropdownData?.data?.map(item => item.id) || [];
            const allNames = getCAbandDropdownData?.data?.map(item => item.name) || [];
            setTempFilterData(prev => ({ ...prev, [key]: allIds }));
            setTempFilterDataName(prev => ({ ...prev, [key]: allNames }));
        } else {
            setTempFilterData(prev => ({ ...prev, [key]: [] }));
            setTempFilterDataName(prev => ({ ...prev, [key]: [] }));
        }
    };

    const renderFilters = type => {
        const systemFilters = {
            trade: { name: "Trade" },
            system: { name: "System" }
        };
        const tradeData = getCAbandDropdownData?.data || [];
        const dropdownLoading = getCAbandDropdownData?.loading;
        return (
            <>
                {Object.keys(systemFilters).map((fil, index) => (
                    <div className={`otr-form col-md-2 p-0 d-flex ${index === Object.keys(systemFilters).length - 1 ? "ml-2" : ""}`}>
                        <div className={`selct-otr col-md-12 p-0`}>
                            <div className="grid-inner d-flex category-selc-drp">
                                <div
                                    className={`grid-box position-relative ${filterData[fil]?.length ? "bg-th-filtered" : ""}`}
                                    onClick={() => handleClickFilterDropdown(fil, fil)}
                                >
                                    <div className={`dropdown-container ${showDropdown === fil ? "dropdown-open" : ""} `}>
                                        <div className="dropdown-toggle click-dropdown">
                                            {systemFilters[fil].name}
                                            <span className="close-reg">
                                                <i className="fas fa-chevron-down" />
                                            </span>
                                        </div>
                                        {showDropdown === fil && (
                                            <div className={`dropdown-menu p-0 ${showDropdown === fil ? "dropdown-active" : ""}`}>
                                                <div className="drop-filtr" onClick={e => e.stopPropagation()}>
                                                    <div className="col-md-12 p-0 border-bottom pt-1">
                                                        <span className="dropdown-item build_search_drp">
                                                            <input
                                                                type="search"
                                                                placeholder="Search..."
                                                                value={filterSearch}
                                                                onChange={e => {
                                                                    let value = e.target.value;
                                                                    setFilterSearch(value);
                                                                }}
                                                            />
                                                            <i
                                                                className="fas fa-times cursor-pointer cls-close"
                                                                onClick={() => setFilterSearch("")}
                                                            />
                                                        </span>
                                                    </div>
                                                    <div className="col-md-12 p-0 border-bottom pt-1">
                                                        <div className="dropdown-item select_section_outer">
                                                            <label className={`container-check ${isView || isSkeletonView ? "cursor-disabled" : ""}`}>
                                                                Select All
                                                                <input
                                                                    type="checkbox"
                                                                    checked={
                                                                        tradeData?.length > 0 && tempFilterData[fil]?.length === tradeData.length
                                                                    }
                                                                    onChange={e => handleSelectAll(fil, e.target.checked)}
                                                                    disabled={isView || isSkeletonView}
                                                                />
                                                                <span
                                                                    className={`checkmark ${isView || isSkeletonView ? "cursor-disabled" : ""}`}
                                                                ></span>
                                                            </label>
                                                            <button
                                                                className={`clear-btn-selection ${isView || isSkeletonView ? "cursor-disabled" : ""}`}
                                                                onClick={() => {
                                                                    setTempFilterData(prev => ({ ...prev, [fil]: [] }));
                                                                    setTempFilterDataName(prev => ({ ...prev, [fil]: [] }));
                                                                }}
                                                                disabled={isView || isSkeletonView}
                                                            >
                                                                Clear
                                                            </button>
                                                        </div>
                                                    </div>
                                                    {dropdownLoading ? (
                                                        <div className="col-md-12 p-4">
                                                            <Loader />
                                                        </div>
                                                    ) : (
                                                        <>
                                                            <div className="col-md-12 p-0 slt-ara border-bottom dpr-wide-txt">
                                                                {tradeData
                                                                    ?.filter(fi => tempFilterData[fil]?.includes(fi.id))
                                                                    ?.map(data => (
                                                                        <span className="dropdown-item" key={data.id}>
                                                                            <label
                                                                                className={`container-check ${
                                                                                    isView || isSkeletonView ? "cursor-disabled" : ""
                                                                                }`}
                                                                            >
                                                                                <span
                                                                                    className="text-short"
                                                                                    data-tip={
                                                                                        data.name?.length + data?.description?.length > 25
                                                                                            ? `${data.name} ${
                                                                                                  data.description ? `(${data.description})` : ""
                                                                                              }`
                                                                                            : null
                                                                                    }
                                                                                    data-effect="solid"
                                                                                    data-for="smart-chart-master-filter"
                                                                                    data-place="left"
                                                                                    data-background-color="#007bff"
                                                                                    data-delay-show="500"
                                                                                >
                                                                                    {data.trade ? `${data.trade}-` : ""}
                                                                                    {data.name} {data.description ? `(${data.description})` : ""}
                                                                                </span>
                                                                                {data.count ? (
                                                                                    <span className="count-num"> ({data.count})</span>
                                                                                ) : null}
                                                                                <input
                                                                                    type="checkbox"
                                                                                    checked={true}
                                                                                    onChange={() => handleSelectDropdown(fil, data)}
                                                                                    disabled={isView || isSkeletonView}
                                                                                />
                                                                                <span
                                                                                    className={`checkmark ${
                                                                                        isView || isSkeletonView ? "cursor-disabled" : ""
                                                                                    }`}
                                                                                />
                                                                            </label>
                                                                        </span>
                                                                    ))}
                                                            </div>
                                                            {renderFilteredList()?.filter(fi => !tempFilterData[fil]?.includes(fi.id))?.length ? (
                                                                <div className="col-md-12 p-0 slt-ara overflw-ht dpr-wide-txt">
                                                                    {renderFilteredList()
                                                                        ?.filter(fi => !tempFilterData[fil]?.includes(fi.id))
                                                                        ?.map(data => (
                                                                            <span className="dropdown-item" key={data.id}>
                                                                                <label
                                                                                    className={`container-check ${
                                                                                        isView || isSkeletonView ? "cursor-disabled" : ""
                                                                                    }`}
                                                                                >
                                                                                    <span
                                                                                        className="text-short"
                                                                                        data-tip={
                                                                                            data.name?.length + data?.description?.length > 25
                                                                                                ? `${data.name} ${
                                                                                                      data.description ? `(${data.description})` : ""
                                                                                                  }`
                                                                                                : null
                                                                                        }
                                                                                        data-effect="solid"
                                                                                        data-for="smart-chart-master-filter"
                                                                                        data-place="left"
                                                                                        data-background-color="#007bff"
                                                                                        data-delay-show="500"
                                                                                    >
                                                                                        {data.trade ? `${data.trade}-` : ""}
                                                                                        {data.name} {data.description ? `(${data.description})` : ""}
                                                                                    </span>
                                                                                    {data.count ? (
                                                                                        <span className="count-num"> ({data.count})</span>
                                                                                    ) : null}
                                                                                    <input
                                                                                        type="checkbox"
                                                                                        checked={false}
                                                                                        onChange={() => handleSelectDropdown(fil, data)}
                                                                                        disabled={isView || isSkeletonView}
                                                                                    />
                                                                                    <span
                                                                                        className={`checkmark ${
                                                                                            isView || isSkeletonView ? "cursor-disabled" : ""
                                                                                        }`}
                                                                                    />
                                                                                </label>
                                                                            </span>
                                                                        ))}
                                                                </div>
                                                            ) : (
                                                                <div className={`col-md-12`}>
                                                                    {renderFilteredList("trade")?.filter(fi => !tempFilterData[fil]?.includes(fi.id))
                                                                        ?.length === 0
                                                                        ? "No data Found"
                                                                        : ""}
                                                                </div>
                                                            )}
                                                        </>
                                                    )}
                                                    <div className="col-md-12 drp-btn">
                                                        <button
                                                            type="button"
                                                            className={`btn btn-primary ${isView || isSkeletonView ? "cursor-disabled" : ""}`}
                                                            onClick={() => handleDropdownAction(fil, "submit")}
                                                            disabled={isView || isSkeletonView}
                                                        >
                                                            ok
                                                        </button>
                                                        <button
                                                            type="button"
                                                            className="btn btn-primary btnClr ml-2"
                                                            onClick={() => handleDropdownAction(fil, "cancel")}
                                                        >
                                                            Cancel
                                                        </button>
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                ))}
            </>
        );
    };

    const handleDropdownAction = (key, action) => {
        if (action === "submit") {
            setFilterData(prev => ({ ...prev, [key]: tempFilterData[key] || [] }));
            setFilterDataName(prev => ({ ...prev, [key]: tempFilterDataName[key] || [] }));
        } else {
            setTempFilterData(prev => ({ ...prev, [key]: filterData[key] || [] }));
            setTempFilterDataName(prev => ({ ...prev, [key]: filterDataName[key] || [] }));
        }
        setShowDropdown("");
    };

    const handleColorPickerToggle = () => {
        setColorModalState(prevColorModalState => (prevColorModalState === "top_n" ? null : "top_n"));

        setTimeout(() => {
            colorPickerRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" });
        }, 100);
    };
    const exportDataSave = (settingsId, groupingData, isGrouping) => {
        if (setSettingsChange) {
            setSettingsChange(true);
        }
        if (isSortedRecommendation) {
            setChartConfigData(prevConfig => {
                return {
                    ...prevConfig,
                    band1: {
                        ...prevConfig?.band1,
                        settings_id: settingsId
                    }
                };
            });
        } else if (hasTableConfigExport) {
            setChartConfigData(prevConfig => {
                return {
                    ...prevConfig,
                    settings_id: settingsId
                };
            });
        } else
            setChartConfigData(prevConfig => {
                return {
                    ...prevConfig,
                    normal_recom: {
                        ...prevConfig["normal_recom"],
                        band1: {
                            ...prevConfig["normal_recom"]?.band1,
                            settings_id: settingsId,
                            ...(isGrouping && { grouping: groupingData })
                        }
                    }
                };
            });
    };
    const exportSettingsModal = () => {
        const isSortedExportSettingsView = showViewModalExport?.type === "sortedRecommendation";
        const projectIdForSmartChart =
            chartData?.properties_text?.project?.band1?.mfilter?.project_ids?.[0] ||
            configData?.selectedChartConfig?.normal_recom?.band1?.mfilter?.project_ids?.[0] ||
            configData?.selectedChartConfig?.band1?.mfilter?.project_ids?.[0];
        const currentSettingId = hasTableConfigExport
            ? chartConfigData?.settings_id
            : isSortedExportSettingsView
              ? chartConfigData?.band1?.settings_id
              : chartConfigData?.normal_recom?.band1?.settings_id;
        const sortedRecommendationExportParams = isSortedExportSettingsView
            ? {
                  project_id: projectIdForSmartChart,
                  client_id: selectedClient,
                  is_smart_chart: true,
                  ...(currentSettingId
                      ? { settings_id: currentSettingId }
                      : {
                            create_new: true
                        })
              }
            : {};
        return showViewModalExport ? (
            <Portal
                body={
                    <ViewExportModal
                        keys={tableData.keys}
                        config={tableData.config}
                        tableData={tableData}
                        onCancel={() => setShowViewModalExport(false)}
                        projectIdForSmartChart={projectIdForSmartChart}
                        isSmartChartView={isSortedExportSettingsView ? false : true}
                        clientIdForSmartChart={selectedClient}
                        setNormalRecomData={({ settingsId, groupingData, isGrouping }) => {
                            exportDataSave(settingsId, groupingData, isGrouping);
                        }}
                        smartChartTileConfig={isSortedExportSettingsView ? chartConfigData : (chartConfigData?.normal_recom ?? chartConfigData)}
                        settingsId={currentSettingId}
                        grouping={hasTableConfigExport || isSortedExportSettingsView ? false : true}
                        hasNoLegacyExport={hasTableConfigExport || isSortedExportSettingsView ? true : false}
                        heading={hasTableConfigExport ? "Column Settings" : ""}
                        hasNoExportDropdowns={hasTableConfigExport ? true : false}
                        smartchartTileKey={configData?.chartKey ?? ""}
                        viewOnly={isSkeletonView || isView}
                        isExportSettingsView={isSortedExportSettingsView ? true : false}
                        shouldSaveExportSettingsId={isSortedExportSettingsView ? true : false}
                        is_smart_chart={isSortedExportSettingsView ? true : false}
                        create_new={isSortedExportSettingsView ? true : false}
                        setExportSettingsData={({ settingsId, groupingData, isGrouping }) => {
                            exportDataSave(settingsId, groupingData, isGrouping);
                        }}
                    />
                }
                onCancel={() => {}}
            />
        ) : null;
    };
    const showColumnConfig =
        (showMore === "normal_recom" && showMore !== "table_view" && showMore?.length > 0) || (showMore === "table_view" && hasTableConfigExport);
    return (
        <>
            <div
                className={`modal modal-region smart-chart-popup ${
                    configData?.chartKey === "sorted_recom" || configData?.chartKey === "energy_band" ? "sm-sorted-recom" : ""
                }`}
                id="modalId"
                tabindex="-1"
                style={{ display: "block" }}
            >
                <div className="modal-dialog" role="document">
                    <OuterDraggable handle=".draggable" cancel=".not-draggable" onDrag={handleDrag}>
                        <div className="modal-content outerModal_draggable" style={transform}>
                            <div className="modal-header draggable">
                                <h5 className="modal-title" id="exampleModalLabel">
                                    <div className="txt-hed d-flex align-items-center">
                                        {getDisplayNameFromKey(configData?.chartKey?.includes("multisystem_") ? "system" : configData?.chartKey)}
                                    </div>
                                    <div className="sys-wrap">
                                        {configData?.chartKey === "sorted_recom" && (
                                            <div className="check-box-area">
                                                <label className={`container-check ${isView || isSkeletonView ? "cursor-diabled" : ""} green-check`}>
                                                    Include Project Cost Total
                                                    <input
                                                        type="checkbox"
                                                        name="is_bold"
                                                        checked={isSoftCost}
                                                        onClick={e =>
                                                            !isView &&
                                                            !isSkeletonView &&
                                                            setIsSoftCost(prevValue => {
                                                                return !prevValue;
                                                            })
                                                        }
                                                    />
                                                    <span className="checkmark"></span>
                                                </label>
                                            </div>
                                        )}
                                        {INDV_BUILDING_EXPORT_ENTITIES.includes(
                                            configData?.chartKey?.includes("multisystem_") ? "system" : configData?.chartKey
                                        ) ? (
                                            <div className="check-box-area">
                                                <label className={`container-check ${isView || isSkeletonView ? "cursor-diabled" : ""} green-check`}>
                                                    Repeat for EVERY building in scope
                                                    <input
                                                        type="checkbox"
                                                        name="is_bold"
                                                        checked={isIndividualExport}
                                                        onClick={e =>
                                                            !isView &&
                                                            !isSkeletonView &&
                                                            setIsIndividualExport(prevValue => {
                                                                return !prevValue;
                                                            })
                                                        }
                                                    />
                                                    <span className="checkmark"></span>
                                                </label>
                                            </div>
                                        ) : null}
                                        {(configData?.chartKey?.includes("multisystem_") || configData?.chartKey === "system") && (
                                            <div className="check-box-area">
                                                <label className={`container-check ${isView || isSkeletonView ? "cursor-diabled" : ""} green-check`}>
                                                    Repeat for EVERY trades in scope
                                                    <input
                                                        type="checkbox"
                                                        name="is_bold"
                                                        checked={isIndividualTradeExport}
                                                        onClick={e =>
                                                            !isView && !isSkeletonView && setIsIndividualTradeExport(prevValue => !prevValue)
                                                        }
                                                    />
                                                    <span className="checkmark"></span>
                                                </label>
                                            </div>
                                        )}
                                        <div className="config-top-rgt-cont">
                                            {!isExcelExport && (
                                                <div className="check-box-area">
                                                    {configData?.chartKey !== "sorted_recom" && configData?.chartKey !== "energy_band" ? (
                                                        <label
                                                            className={`container-check ${
                                                                isView || isSkeletonView ? "cursor-diabled" : ""
                                                            } green-check`}
                                                        >
                                                            {" "}
                                                            Select All Charts
                                                            <input
                                                                type="checkbox"
                                                                name="is_bold"
                                                                checked={isAllSelected()}
                                                                onClick={e =>
                                                                    !isView &&
                                                                    !isSkeletonView &&
                                                                    handleChartConfigData("allSelect", "", { is_remove: !e.target.checked })
                                                                }
                                                            />
                                                            <span className="checkmark"></span>
                                                        </label>
                                                    ) : null}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </h5>
                                <button type="button" className="close" onClick={() => handleClickCancel()}>
                                    <span aria-hidden="true">
                                        <img src="/img/close.svg" alt="" />
                                    </span>
                                </button>
                            </div>
                            <div className="modal-body region-otr draggable">
                                <div className="upload-area not-draggable">
                                    <div className="upload-sec cursor-hand" role="button" tabindex="0">
                                        <input type="file" multiple="" autocomplete="off" tabindex="-1" style={{ display: "none" }} />
                                    </div>
                                </div>
                                <DragDropContext onDragEnd={onDragEnd}>
                                    {configData?.chartKey !== "sorted_recom" && configData?.chartKey !== "energy_band" ? (
                                        <>
                                            {(configData?.chartKey?.includes("multisystem_") || configData?.chartKey === "system") && (
                                                <div className="wrapper-top-system">
                                                    <div className="mr-2 pr-0 number-text-block sm-charts-top-n-inp not-draggable col-md-2">
                                                        <NumberFormat
                                                            className="custom-input form-control"
                                                            value={systemTopCombination}
                                                            prefix="Top "
                                                            suffix=" +1"
                                                            placeholder="Top n +1"
                                                            displayType="input"
                                                            decimalScale={0}
                                                            allowNegative={false}
                                                            onValueChange={values =>
                                                                setSystemTopCombination(
                                                                    Number.isNaN(parseInt(values.value, 10)) ? null : parseInt(values.value, 10)
                                                                )
                                                            }
                                                        />
                                                    </div>
                                                    {renderFilters()}
                                                </div>
                                            )}
                                            <div className="col-md-12 sm-chart-views pl-1 not-draggable">
                                                <div className="left-box-sm">
                                                    {Array(Math.round(defChartView.length / 2))
                                                        .fill()
                                                        .map((dropRow, rowIndex) => (
                                                            <Droppable
                                                                droppableId={`CHART-VIEW-${rowIndex}`}
                                                                direction="vertical"
                                                                isDropDisabled={isView || isSkeletonView || chartViews.length < 2 ? true : false}
                                                            >
                                                                {(provided, snapshot) => (
                                                                    <div
                                                                        className="dragble-area"
                                                                        ref={provided.innerRef}
                                                                        {...provided.droppableProps}
                                                                    >
                                                                        {chartViews.slice(rowIndex * 2, (rowIndex + 1) * 2).map((view, index) => (
                                                                            <>
                                                                                {configData?.itemConfig?.[view] ? (
                                                                                    <Draggable
                                                                                        key={`draggable-chart-view-${rowIndex}${index}`}
                                                                                        draggableId={`draggable-chart-view-${rowIndex}${index}`}
                                                                                        index={rowIndex * 2 + index}
                                                                                        isDragDisabled={
                                                                                            isView || isSkeletonView || chartViews.length < 2
                                                                                                ? true
                                                                                                : false || modalDraggable
                                                                                        }
                                                                                    >
                                                                                        {provided => (
                                                                                            <div
                                                                                                className="box-drg-outr"
                                                                                                ref={provided.innerRef}
                                                                                                {...provided.draggableProps}
                                                                                                {...provided.dragHandleProps}
                                                                                                key={`draggable-${index}`}
                                                                                            >
                                                                                                <div
                                                                                                    className={`drag-item height-item ${
                                                                                                        showMore === view &&
                                                                                                        !(
                                                                                                            (errorMessage?.detailed_view?.message &&
                                                                                                                view === "detailed_view") ||
                                                                                                            (errorMessage?.summary_view?.message &&
                                                                                                                view === "summary_view")
                                                                                                        )
                                                                                                            ? "current-active "
                                                                                                            : ""
                                                                                                    }${
                                                                                                        (errorMessage?.detailed_view?.message &&
                                                                                                            view === "detailed_view") ||
                                                                                                        (errorMessage?.summary_view?.message &&
                                                                                                            view === "summary_view")
                                                                                                            ? "error-border"
                                                                                                            : ""
                                                                                                    }`}
                                                                                                    onClick={() => {
                                                                                                        setShowMore(view);
                                                                                                    }}
                                                                                                >
                                                                                                    <div className="top-sec sq-ic-out">
                                                                                                        <div className="icon">
                                                                                                            <img src="/img/icon-squre.svg" alt="" />
                                                                                                        </div>
                                                                                                    </div>

                                                                                                    <div className="cont-sec">
                                                                                                        <div className="icons-sec">
                                                                                                            <img
                                                                                                                src={`/img/${getIconsForView(view)}`}
                                                                                                                alt=""
                                                                                                            />
                                                                                                        </div>
                                                                                                        <h3>{getDisplayNameFromKey(view)}</h3>
                                                                                                    </div>
                                                                                                    <div className="top-sec">
                                                                                                        <div className="icon-pop-flex">
                                                                                                            <div className="checkbox">
                                                                                                                <label
                                                                                                                    className={`container-check ${
                                                                                                                        isView ||
                                                                                                                        isSkeletonView ||
                                                                                                                        isExcelExport
                                                                                                                            ? "cursor-diabled"
                                                                                                                            : ""
                                                                                                                    } green-check`}
                                                                                                                >
                                                                                                                    <input
                                                                                                                        type="checkbox"
                                                                                                                        name="is_bold"
                                                                                                                        checked={
                                                                                                                            view === "table_view"
                                                                                                                                ? chartConfigData?.hasOwnProperty(
                                                                                                                                      "table_view"
                                                                                                                                  ) ?? false
                                                                                                                                : view ===
                                                                                                                                  "normal_recom"
                                                                                                                                ? chartConfigData?.[
                                                                                                                                      view
                                                                                                                                  ]?.hasOwnProperty(
                                                                                                                                      "band1"
                                                                                                                                  ) ?? false
                                                                                                                                : chartConfigData?.[
                                                                                                                                      view
                                                                                                                                  ]?.hasOwnProperty(
                                                                                                                                      "chart_type"
                                                                                                                                  ) ?? false
                                                                                                                        }
                                                                                                                        onClick={e =>
                                                                                                                            !isView &&
                                                                                                                            !isSkeletonView &&
                                                                                                                            handleChartConfigData(
                                                                                                                                "view",
                                                                                                                                view,
                                                                                                                                {
                                                                                                                                    is_remove:
                                                                                                                                        !e.target
                                                                                                                                            .checked
                                                                                                                                }
                                                                                                                            )
                                                                                                                        }
                                                                                                                        disabled={
                                                                                                                            isView ||
                                                                                                                            isSkeletonView ||
                                                                                                                            isExcelExport
                                                                                                                        }
                                                                                                                    />
                                                                                                                    <span className="checkmark"></span>
                                                                                                                </label>
                                                                                                            </div>
                                                                                                        </div>
                                                                                                    </div>
                                                                                                </div>

                                                                                                {provided.placeholder}
                                                                                            </div>
                                                                                        )}
                                                                                    </Draggable>
                                                                                ) : null}
                                                                            </>
                                                                        ))}
                                                                    </div>
                                                                )}
                                                            </Droppable>
                                                        ))}
                                                </div>
                                                <div className="fot-sec top-border types-otr-chart-sec overflw-scrl">
                                                    <div className="header-top-txt">
                                                        <img src={viewIcon} alt="icon" />
                                                        <h4>{getDisplayNameFromKey(showMore)} Settings</h4>
                                                    </div>
                                                    <div className="fot-select">
                                                        <label>Heading</label>
                                                        <div className="check-box-area">
                                                            <label
                                                                className={`container-check ${
                                                                    isView || isSkeletonView ? "cursor-diabled" : ""
                                                                } green-check`}
                                                            >
                                                                Yes
                                                                <input
                                                                    type="checkbox"
                                                                    name="is_bold"
                                                                    checked={heading?.[showMore]}
                                                                    onClick={() => {
                                                                        setHeading({ ...heading, [showMore]: true });
                                                                    }}
                                                                    disabled={isSkeletonView || isView}
                                                                />
                                                                <span className="checkmark"></span>
                                                            </label>
                                                        </div>
                                                        <div className="check-box-area">
                                                            <label
                                                                className={`container-check ${
                                                                    isView || isSkeletonView ? "cursor-diabled" : ""
                                                                } green-check`}
                                                            >
                                                                No
                                                                <input
                                                                    type="checkbox"
                                                                    name="is_bold"
                                                                    checked={!heading?.[showMore]}
                                                                    onClick={() => {
                                                                        setHeading({ ...heading, [showMore]: false });
                                                                    }}
                                                                    disabled={isSkeletonView || isView}
                                                                />
                                                                <span className="checkmark"></span>
                                                            </label>
                                                        </div>
                                                    </div>
                                                    {heading?.[showMore] && (
                                                        <>
                                                            <div className="fot-select">
                                                                <label>Heading type</label>
                                                                <div className="check-box-area">
                                                                    <label
                                                                        className={`container-check ${
                                                                            isView || isSkeletonView ? "cursor-diabled" : ""
                                                                        } green-check`}
                                                                    >
                                                                        Custom Heading
                                                                        <input
                                                                            type="checkbox"
                                                                            name="is_bold"
                                                                            checked={isCustomTypeHeading?.[showMore]}
                                                                            onClick={() => {
                                                                                setIsCustomTypeHeading({ ...isCustomTypeHeading, [showMore]: true });
                                                                            }}
                                                                            disabled={isSkeletonView || isView}
                                                                        />
                                                                        <span className="checkmark"></span>
                                                                    </label>
                                                                </div>
                                                                <div className="check-box-area">
                                                                    <label
                                                                        className={`container-check ${
                                                                            isView || isSkeletonView ? "cursor-diabled" : ""
                                                                        } green-check`}
                                                                    >
                                                                        Predefined Heading
                                                                        <input
                                                                            type="checkbox"
                                                                            name="is_bold"
                                                                            checked={!isCustomTypeHeading?.[showMore]}
                                                                            onClick={() => {
                                                                                setIsCustomTypeHeading({ ...isCustomTypeHeading, [showMore]: false });
                                                                            }}
                                                                            disabled={isSkeletonView || isView}
                                                                        />
                                                                        <span className="checkmark"></span>
                                                                    </label>
                                                                </div>
                                                            </div>
                                                            <div class="card p-0 mb-2">
                                                                <div class="card-header" id="headingOne">
                                                                    <div
                                                                        class="d-flex content-block-2 otr-recom-div no-bottom-bord btn"
                                                                        data-toggle="collapse"
                                                                        data-target="#collapseOne"
                                                                        aria-expanded="true"
                                                                        aria-controls="collapseOne"
                                                                    >
                                                                        <button
                                                                            className="btn heading-btn-accordion"
                                                                            data-toggle="collapse"
                                                                            data-target="#collapseOne"
                                                                            aria-expanded="true"
                                                                            aria-controls="collapseOne"
                                                                        >
                                                                            Heading Styles
                                                                        </button>
                                                                    </div>
                                                                </div>

                                                                <div
                                                                    id="collapseOne"
                                                                    className="collapse show "
                                                                    aria-labelledby="headingOne"
                                                                    data-parent="#accordion"
                                                                >
                                                                    <div class="card-body p-0 pt-2">
                                                                        <TileCustomHeading
                                                                            setHeadingStyleData={setHeadingStyleData}
                                                                            chartView={showMore}
                                                                            headingStyleData={headingStyleData}
                                                                            isCustomTypeHeading={isCustomTypeHeading}
                                                                            error={error}
                                                                            viewOnly={isView}
                                                                        />
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </>
                                                    )}
                                                    {showMore !== "table_view" && showMore?.length > 0 ? (
                                                        <div className="wrapper-fot-select">
                                                            <div className="fot-select">
                                                                <label>Page Break</label>
                                                                <div className="check-box-area">
                                                                    <label
                                                                        className={`container-check ${
                                                                            isView || isSkeletonView ? "cursor-diabled" : ""
                                                                        } green-check`}
                                                                    >
                                                                        Yes
                                                                        <input
                                                                            type="checkbox"
                                                                            name="is_bold"
                                                                            checked={
                                                                                showMore === "table_view"
                                                                                    ? !tablePageBreak
                                                                                    : showMore === "summary_view"
                                                                                    ? !summaryPageBreak
                                                                                    : showMore === "normal_recom"
                                                                                    ? !recomendationPageBreak
                                                                                    : !detailPageBreak
                                                                            }
                                                                            onClick={e => {
                                                                                if (showMore === "summary_view") {
                                                                                    setSummaryPageBreak(prevValue => !prevValue);
                                                                                    handleChartConfigData("pageBreak", showMore, {
                                                                                        is_remove: !e.target.checked,
                                                                                        pageBreak: !summaryPageBreak
                                                                                    });
                                                                                } else if (showMore === "detailed_view") {
                                                                                    setDetailPageBreak(prevValue => !prevValue);
                                                                                    handleChartConfigData("pageBreak", showMore, {
                                                                                        is_remove: !e.target.checked,
                                                                                        pageBreak: !detailPageBreak
                                                                                    });
                                                                                } else if (showMore === "normal_recom") {
                                                                                    setRecomendationPageBreak(prevValue => !prevValue);
                                                                                    handleChartConfigData("pageBreak", showMore, {
                                                                                        is_remove: !e.target.checked,
                                                                                        pageBreak: !recomendationPageBreak
                                                                                    });
                                                                                }
                                                                            }}
                                                                            disabled={isSkeletonView || isView}
                                                                        />
                                                                        <span className="checkmark"></span>
                                                                    </label>
                                                                </div>
                                                                <div className="check-box-area">
                                                                    <label
                                                                        className={`container-check ${
                                                                            isView || isSkeletonView ? "cursor-diabled" : ""
                                                                        } green-check`}
                                                                    >
                                                                        No
                                                                        <input
                                                                            type="checkbox"
                                                                            name="is_bold"
                                                                            checked={
                                                                                showMore === "table_view"
                                                                                    ? tablePageBreak
                                                                                    : showMore === "summary_view"
                                                                                    ? summaryPageBreak
                                                                                    : showMore === "normal_recom"
                                                                                    ? recomendationPageBreak
                                                                                    : detailPageBreak
                                                                            }
                                                                            onClick={e => {
                                                                                if (showMore === "summary_view") {
                                                                                    setSummaryPageBreak(prevValue => !prevValue);
                                                                                    handleChartConfigData("pageBreak", showMore, {
                                                                                        is_remove: !e.target.checked,
                                                                                        pageBreak: !summaryPageBreak
                                                                                    });
                                                                                } else if (showMore === "detailed_view") {
                                                                                    setDetailPageBreak(prevValue => !prevValue);
                                                                                    handleChartConfigData("pageBreak", showMore, {
                                                                                        is_remove: !e.target.checked,
                                                                                        pageBreak: !detailPageBreak
                                                                                    });
                                                                                } else if (showMore === "normal_recom") {
                                                                                    setRecomendationPageBreak(prevValue => !prevValue);
                                                                                    handleChartConfigData("pageBreak", showMore, {
                                                                                        is_remove: !e.target.checked,
                                                                                        pageBreak: !recomendationPageBreak
                                                                                    });
                                                                                }
                                                                            }}
                                                                            disabled={isSkeletonView || isView}
                                                                        />
                                                                        <span className="checkmark"></span>
                                                                    </label>
                                                                </div>
                                                            </div>
                                                            {showMore !== "normal_recom" && (
                                                                <>
                                                                    <div className="fot-select">
                                                                        <label>Chart Types</label>
                                                                        {renderChartTypes(showMore, configData.chartKey).map((chartType, i) => (
                                                                            <div className="check-box-area" key={i}>
                                                                                <label
                                                                                    className={`container-check ${
                                                                                        isView || isSkeletonView ? "cursor-diabled" : ""
                                                                                    } green-check`}
                                                                                >
                                                                                    {getDisplayNameFromKey(chartType)}
                                                                                    <input
                                                                                        type="checkbox"
                                                                                        name="is_bold"
                                                                                        checked={
                                                                                            chartConfigData?.[showMore]?.chart_type?.includes(
                                                                                                chartType
                                                                                            )
                                                                                                ? true
                                                                                                : false
                                                                                        }
                                                                                        onClick={e =>
                                                                                            !isView &&
                                                                                            !isSkeletonView &&
                                                                                            handleChartConfigData("chartType", showMore, {
                                                                                                is_remove: !e.target.checked,
                                                                                                chartType: chartType
                                                                                            })
                                                                                        }
                                                                                    />
                                                                                    <span className="checkmark"></span>
                                                                                </label>
                                                                            </div>
                                                                        ))}
                                                                    </div>

                                                                    {renderSortOrderButtons(showMore)}

                                                                    {configData.chartKey === "EFCI" && (
                                                                        <div className="fot-select">
                                                                            <label>Funding Options</label>
                                                                            {[1, 2, 3, 4].map((fo, i) => (
                                                                                <div className="check-box-area" key={i}>
                                                                                    <label
                                                                                        className={`container-check ${
                                                                                            isView || isSkeletonView ? "cursor-diabled" : ""
                                                                                        } green-check`}
                                                                                    >
                                                                                        {`FO ${fo}`}
                                                                                        <input
                                                                                            type="checkbox"
                                                                                            name="is_bold"
                                                                                            checked={
                                                                                                !!chartConfigData?.[
                                                                                                    showMore
                                                                                                ]?.config?.funding_source?.includes(fo)
                                                                                            }
                                                                                            onClick={e =>
                                                                                                !isView &&
                                                                                                !isSkeletonView &&
                                                                                                handleChartConfigData("fundingOption", showMore, {
                                                                                                    is_remove: !e.target.checked,
                                                                                                    chartType: fo
                                                                                                })
                                                                                            }
                                                                                        />
                                                                                        <span className="checkmark"></span>
                                                                                    </label>
                                                                                </div>
                                                                            ))}
                                                                        </div>
                                                                    )}
                                                                    <div className="fot-select">
                                                                        <label>Options</label>
                                                                        {EXPORTING_CHART_SECTIONS.map((exportChartSection, i) => (
                                                                            <div className="check-box-area" key={i}>
                                                                                <label
                                                                                    className={`container-check ${
                                                                                        isView || isSkeletonView ? "cursor-diabled" : ""
                                                                                    } green-check`}
                                                                                >
                                                                                    {exportChartSection.label}
                                                                                    <input
                                                                                        type="checkbox"
                                                                                        name="is_bold"
                                                                                        checked={
                                                                                            chartConfigData?.[showMore]?.config?.[
                                                                                                exportChartSection.key
                                                                                            ] || false
                                                                                        }
                                                                                        onClick={e =>
                                                                                            !isView &&
                                                                                            !isSkeletonView &&
                                                                                            handleChartConfigData("exportChartSection", showMore, {
                                                                                                is_remove: !e.target.checked,
                                                                                                exportChartSectionType: exportChartSection.key
                                                                                            })
                                                                                        }
                                                                                    />
                                                                                    <span className="checkmark"></span>
                                                                                </label>
                                                                            </div>
                                                                        ))}

                                                                        {(configData?.chartKey?.includes("multisystem_") ||
                                                                            configData?.chartKey === "system") &&
                                                                            systemTopCombination && (
                                                                                <div className="font-color font-st col-md-12 pl-0 other-colour-wrapper">
                                                                                    <div className="head_sub">
                                                                                        <label>Others Color (Top n+1)</label>
                                                                                    </div>
                                                                                    <div className="dropdown">
                                                                                        <div
                                                                                            className="dropdown-toggle"
                                                                                            type="button"
                                                                                            onClick={handleColorPickerToggle}
                                                                                        >
                                                                                            <div className="color-bx-wrapper d-flex align-items-center">
                                                                                                {chartConfigData?.[showMore]?.config?.[
                                                                                                    "others_color"
                                                                                                ] || "Select"}

                                                                                                <span
                                                                                                    className="color-bx"
                                                                                                    style={{
                                                                                                        background: chartConfigData?.[showMore]
                                                                                                            ?.config?.["others_color"]
                                                                                                            ? `${chartConfigData?.[showMore]?.config?.["others_color"]}`
                                                                                                            : "#fff"
                                                                                                    }}
                                                                                                ></span>
                                                                                            </div>

                                                                                            <i
                                                                                                aria-hidden="true"
                                                                                                className="grey chevron down icon arrow_right"
                                                                                            ></i>
                                                                                        </div>
                                                                                        {colorModalState === "top_n" && (
                                                                                            <div ref={colorPickerRef} style={styles.popover}>
                                                                                                <div
                                                                                                    style={styles.cover}
                                                                                                    onClick={() => setColorModalState(null)}
                                                                                                />
                                                                                                <SketchPicker
                                                                                                    position="right"
                                                                                                    color={
                                                                                                        chartConfigData?.[showMore]?.config?.[
                                                                                                            "others_color"
                                                                                                        ] === "#000000"
                                                                                                            ? ""
                                                                                                            : chartConfigData?.[showMore]?.config?.[
                                                                                                                  "others_color"
                                                                                                              ]
                                                                                                    }
                                                                                                    presetColors={presetColors}
                                                                                                    onChange={color =>
                                                                                                        handleChartConfigData(
                                                                                                            "exportChartSectionOthersColor",
                                                                                                            showMore,
                                                                                                            {
                                                                                                                key: "others_color",
                                                                                                                value: color.hex
                                                                                                            }
                                                                                                        )
                                                                                                    }
                                                                                                />
                                                                                            </div>
                                                                                        )}
                                                                                    </div>
                                                                                </div>
                                                                            )}
                                                                    </div>

                                                                    <div className="fot-select chart-size">
                                                                        <label>Chart size</label>
                                                                        <div className="chart-box-out">
                                                                            <div className="check-box-area">
                                                                                <label
                                                                                    className={`container-check ${
                                                                                        isView || isSkeletonView ? "cursor-diabled" : ""
                                                                                    } green-check`}
                                                                                >
                                                                                    W
                                                                                </label>
                                                                                <NumberFormat
                                                                                    className={`custom-input form-control ${
                                                                                        (errorMessage?.detailed_view?.message &&
                                                                                            errorMessage?.detailed_view?.width &&
                                                                                            showMore === "detailed_view") ||
                                                                                        (errorMessage?.summary_view?.message &&
                                                                                            errorMessage?.summary_view.width &&
                                                                                            showMore === "summary_view")
                                                                                            ? "error-border"
                                                                                            : ""
                                                                                    }`}
                                                                                    placeholder="eg:450"
                                                                                    value={parseFloat(tiles[showMore]?.width)}
                                                                                    thousandSeparator={false}
                                                                                    decimalScale={2}
                                                                                    onValueChange={values => {
                                                                                        const { value } = values;
                                                                                        handleWidthChange(value, showMore);
                                                                                    }}
                                                                                    allowNegative={false}
                                                                                    allowLeadingZeros={false}
                                                                                    isAllowed={({ value }) => {
                                                                                        return (
                                                                                            value === "" ||
                                                                                            (!isNaN(value) && parseFloat(value) <= 1000)
                                                                                        );
                                                                                    }}
                                                                                    onFocus={() => setFocusedInput("width")}
                                                                                    onBlur={() => setFocusedInput(null)}
                                                                                    disabled={isSkeletonView || isView}
                                                                                />
                                                                                <span class="pt-txt">pt</span>
                                                                            </div>
                                                                            <div
                                                                                className={`img-wd ${
                                                                                    tiles[showMore]?.autoCalculate ? "filled-color" : ""
                                                                                }`}
                                                                                onClick={() => handleCheckboxChange(showMore)}
                                                                            >
                                                                                <svg
                                                                                    xmlns="http://www.w3.org/2000/svg"
                                                                                    width="63.614"
                                                                                    height="17.878"
                                                                                    viewBox="0 0 63.614 17.878"
                                                                                >
                                                                                    <g
                                                                                        id="Group_140"
                                                                                        data-name="Group 140"
                                                                                        transform="translate(-1121.636 -679.061)"
                                                                                    >
                                                                                        <path
                                                                                            id="hyperlink-icon"
                                                                                            d="M8.807,4.957A1.113,1.113,0,0,1,7.233,3.383L9.125,1.5a5.148,5.148,0,0,1,7.262,0l.01.012a5.146,5.146,0,0,1-.01,7.25l-1.891,1.884A1.113,1.113,0,1,1,12.921,9.07l1.891-1.891a2.922,2.922,0,0,0,0-4.107L14.8,3.055a2.922,2.922,0,0,0-4.1.01L8.809,4.957Zm2.037.46a1.113,1.113,0,0,1,1.567,1.574L6.99,12.412a1.113,1.113,0,0,1-1.574-1.573l5.423-5.423Zm-1.81,7.54a1.113,1.113,0,0,1,1.573,1.574L8.759,16.38a5.146,5.146,0,0,1-7.24.02l-.041-.039A5.148,5.148,0,0,1,1.5,9.118L3.346,7.274A1.113,1.113,0,1,1,4.921,8.843L3.072,10.692a2.923,2.923,0,0,0,0,4.11h0a2.92,2.92,0,0,0,4.113,0Z"
                                                                                            transform="translate(1144.501 679.062)"
                                                                                            fill="#bdc8dd"
                                                                                        />
                                                                                        <path
                                                                                            id="Line_12"
                                                                                            data-name="Line 12"
                                                                                            d="M12,.75H0A.75.75,0,0,1-.75,0,.75.75,0,0,1,0-.75H12a.75.75,0,0,1,.75.75A.75.75,0,0,1,12,.75Z"
                                                                                            transform="translate(1122.386 688.5)"
                                                                                            fill="#bdc8dd"
                                                                                        />
                                                                                        <path
                                                                                            id="Line_13"
                                                                                            data-name="Line 13"
                                                                                            d="M12,.75H0A.75.75,0,0,1-.75,0,.75.75,0,0,1,0-.75H12a.75.75,0,0,1,.75.75A.75.75,0,0,1,12,.75Z"
                                                                                            transform="translate(1172.5 688.5)"
                                                                                            fill="#bdc8dd"
                                                                                        />
                                                                                    </g>
                                                                                </svg>
                                                                            </div>
                                                                            <div className="check-box-area">
                                                                                <label
                                                                                    className={`container-check ${
                                                                                        isView || isSkeletonView ? "cursor-diabled" : ""
                                                                                    } green-check`}
                                                                                >
                                                                                    H
                                                                                </label>
                                                                                <NumberFormat
                                                                                    className={`custom-input form-control ${
                                                                                        (errorMessage?.detailed_view?.message &&
                                                                                            errorMessage?.detailed_view?.height &&
                                                                                            showMore === "detailed_view") ||
                                                                                        (errorMessage?.summary_view?.message &&
                                                                                            errorMessage?.summary_view?.height &&
                                                                                            showMore === "summary_view")
                                                                                            ? "error-border"
                                                                                            : ""
                                                                                    }`}
                                                                                    placeholder="eg:270"
                                                                                    value={parseFloat(tiles[showMore]?.height)}
                                                                                    thousandSeparator={false}
                                                                                    decimalScale={2}
                                                                                    onValueChange={values => {
                                                                                        const { value } = values;
                                                                                        handleHeightChange(value, showMore);
                                                                                    }}
                                                                                    allowNegative={false}
                                                                                    isAllowed={({ value }) => {
                                                                                        return (
                                                                                            value === "" ||
                                                                                            (!isNaN(value) && parseFloat(value) <= 1000)
                                                                                        );
                                                                                    }}
                                                                                    onFocus={() => setFocusedInput("height")}
                                                                                    onBlur={() => setFocusedInput(null)}
                                                                                    disabled={isSkeletonView || isView}
                                                                                />
                                                                                <span class="pt-txt">pt</span>
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                </>
                                                            )}
                                                        </div>
                                                    ) : (
                                                        showMore === "table_view" && (
                                                            <div className="fot-select">
                                                                <label>Page Break</label>
                                                                <div className="check-box-area">
                                                                    <label
                                                                        className={`container-check ${
                                                                            isView || isSkeletonView ? "cursor-diabled" : ""
                                                                        } green-check`}
                                                                    >
                                                                        Yes
                                                                        <input
                                                                            type="checkbox"
                                                                            name="is_bold"
                                                                            checked={
                                                                                showMore === "table_view"
                                                                                    ? !tablePageBreak
                                                                                    : showMore === "summary_view"
                                                                                    ? !summaryPageBreak
                                                                                    : !detailPageBreak
                                                                            }
                                                                            onClick={e => {
                                                                                if (showMore === "table_view") {
                                                                                    setTablePageBreak(prevValue => !prevValue);
                                                                                } else if (showMore === "summary_view") {
                                                                                    setSummaryPageBreak(prevValue => !prevValue);
                                                                                    handleChartConfigData("pageBreak", showMore, {
                                                                                        is_remove: !e.target.checked,
                                                                                        pageBreak: !summaryPageBreak
                                                                                    });
                                                                                } else if (showMore === "detailed_view") {
                                                                                    setDetailPageBreak(prevValue => !prevValue);
                                                                                    handleChartConfigData("pageBreak", showMore, {
                                                                                        is_remove: !e.target.checked,
                                                                                        pageBreak: !detailPageBreak
                                                                                    });
                                                                                }
                                                                            }}
                                                                            disabled={isSkeletonView || isView}
                                                                        />
                                                                        <span className="checkmark"></span>
                                                                    </label>
                                                                </div>
                                                                <div className="check-box-area">
                                                                    <label
                                                                        className={`container-check ${
                                                                            isView || isSkeletonView ? "cursor-diabled" : ""
                                                                        } green-check`}
                                                                    >
                                                                        No
                                                                        <input
                                                                            type="checkbox"
                                                                            name="is_bold"
                                                                            checked={
                                                                                showMore === "table_view"
                                                                                    ? tablePageBreak
                                                                                    : showMore === "summary_view"
                                                                                    ? summaryPageBreak
                                                                                    : detailPageBreak
                                                                            }
                                                                            onClick={() => {
                                                                                setTablePageBreak(prevValue => !prevValue);
                                                                            }}
                                                                            disabled={isSkeletonView || isView}
                                                                        />
                                                                        <span className="checkmark"></span>
                                                                    </label>
                                                                </div>
                                                            </div>
                                                        )
                                                    )}
                                                    {showColumnConfig && (
                                                        <div className="btn-area fot-select mb-1">
                                                            <label>Column Configuration</label>
                                                            <button
                                                                className="btn btn-conf"
                                                                onClick={() => {
                                                                    setShowViewModalExport(true);
                                                                }}
                                                                // disabled={isSkeletonView || isView}
                                                            >
                                                                Config <img src="/img/blue_arrow_right.svg" alt="" />
                                                            </button>
                                                        </div>
                                                    )}
                                                    {showMore === "table_view" && configData?.chartKey === "project_cost" ? (
                                                        <>
                                                            <div className="fot-select">
                                                                <label>Show Settings Column</label>
                                                                <div>
                                                                    <div className="radio-sec">
                                                                        <div className="button-group d-flex">
                                                                            {/* YES */}
                                                                            <div className="check-box-area">
                                                                                <label
                                                                                    className={`container-check ${
                                                                                        isView || isSkeletonView ? "cursor-diabled" : ""
                                                                                    } green-check`}
                                                                                >
                                                                                    <input
                                                                                        type="radio"
                                                                                        name="showSettings"
                                                                                        checked={!!chartConfigData?.show_columns?.setting}
                                                                                        onChange={e =>
                                                                                            handleChartConfigData("showColumns", null, {
                                                                                                field: "setting",
                                                                                                checked: e.target.checked
                                                                                            })
                                                                                        }
                                                                                        disabled={isView || isSkeletonView}
                                                                                    />
                                                                                    <span className="checkmark" /> Yes
                                                                                </label>
                                                                            </div>

                                                                            {/* NO */}
                                                                            <div className="check-box-area">
                                                                                <label
                                                                                    className={`container-check ${
                                                                                        isView || isSkeletonView ? "cursor-diabled" : ""
                                                                                    } green-check`}
                                                                                >
                                                                                    <input
                                                                                        type="radio"
                                                                                        name="showSettings"
                                                                                        checked={!chartConfigData?.show_columns?.setting}
                                                                                        onChange={e =>
                                                                                            handleChartConfigData("showColumns", null, {
                                                                                                field: "setting",
                                                                                                checked: !e.target.checked
                                                                                            })
                                                                                        }
                                                                                        disabled={isView || isSkeletonView}
                                                                                    />
                                                                                    <span className="checkmark" /> No
                                                                                </label>
                                                                            </div>
                                                                        </div>
                                                                    </div>

                                                                    {chartConfigData?.show_columns?.setting && (
                                                                        <div className="radio-sec">
                                                                            <div className="button-group d-flex">
                                                                                <div className="check-box-area">
                                                                                    <label
                                                                                        className={`container-check ${
                                                                                            isView || isSkeletonView ? "cursor-diabled" : ""
                                                                                        } green-check`}
                                                                                    >
                                                                                        Show Percentage
                                                                                        <input
                                                                                            type="checkbox"
                                                                                            checked={
                                                                                                !!chartConfigData?.show_columns?.Setting?.percentage
                                                                                            }
                                                                                            onChange={e =>
                                                                                                handleChartConfigData("showColumns", null, {
                                                                                                    column: "Setting",
                                                                                                    field: "percentage",
                                                                                                    checked: e.target.checked
                                                                                                })
                                                                                            }
                                                                                            disabled={isView || isSkeletonView}
                                                                                        />
                                                                                        <span className="checkmark" />
                                                                                    </label>
                                                                                </div>

                                                                                <div className="check-box-area">
                                                                                    <label
                                                                                        className={`container-check ${
                                                                                            isView || isSkeletonView ? "cursor-diabled" : ""
                                                                                        } green-check`}
                                                                                    >
                                                                                        Show Amount
                                                                                        <input
                                                                                            type="checkbox"
                                                                                            checked={!!chartConfigData?.show_columns?.Setting?.value}
                                                                                            onChange={e =>
                                                                                                handleChartConfigData("showColumns", null, {
                                                                                                    column: "Setting",
                                                                                                    field: "value",
                                                                                                    checked: e.target.checked
                                                                                                })
                                                                                            }
                                                                                            disabled={isView || isSkeletonView}
                                                                                        />
                                                                                        <span className="checkmark" />
                                                                                    </label>
                                                                                </div>
                                                                            </div>
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            </div>
                                                            <div className="fot-select">
                                                                <label>Show Actual Column</label>
                                                                <div>
                                                                    <div className="radio-sec">
                                                                        <div className="button-group d-flex">
                                                                            <div className="check-box-area">
                                                                                <label
                                                                                    className={`container-check ${
                                                                                        isView || isSkeletonView ? "cursor-diabled" : ""
                                                                                    } green-check`}
                                                                                >
                                                                                    <input
                                                                                        type="radio"
                                                                                        name="showActual"
                                                                                        checked={!!chartConfigData?.show_columns?.actual}
                                                                                        onChange={e =>
                                                                                            handleChartConfigData("showColumns", null, {
                                                                                                field: "actual",
                                                                                                checked: e.target.checked
                                                                                            })
                                                                                        }
                                                                                        disabled={isView || isSkeletonView}
                                                                                    />
                                                                                    <span className="checkmark" /> Yes
                                                                                </label>
                                                                            </div>

                                                                            <div className="check-box-area">
                                                                                <label
                                                                                    className={`container-check ${
                                                                                        isView || isSkeletonView ? "cursor-diabled" : ""
                                                                                    } green-check`}
                                                                                >
                                                                                    <input
                                                                                        type="radio"
                                                                                        name="showActual"
                                                                                        checked={!chartConfigData?.show_columns?.actual}
                                                                                        onChange={e =>
                                                                                            handleChartConfigData("showColumns", null, {
                                                                                                field: "actual",
                                                                                                checked: !e.target.checked
                                                                                            })
                                                                                        }
                                                                                        disabled={isView || isSkeletonView}
                                                                                    />
                                                                                    <span className="checkmark" /> No
                                                                                </label>
                                                                            </div>
                                                                        </div>
                                                                    </div>

                                                                    {chartConfigData?.show_columns?.actual && (
                                                                        <div className="radio-sec">
                                                                            <div className="button-group d-flex">
                                                                                <div className="check-box-area">
                                                                                    <label
                                                                                        className={`container-check ${
                                                                                            isView || isSkeletonView ? "cursor-diabled" : ""
                                                                                        } green-check`}
                                                                                    >
                                                                                        Show Percentage
                                                                                        <input
                                                                                            type="checkbox"
                                                                                            checked={
                                                                                                !!chartConfigData?.show_columns?.Actual?.percentage
                                                                                            }
                                                                                            onChange={e =>
                                                                                                handleChartConfigData("showColumns", null, {
                                                                                                    column: "Actual",
                                                                                                    field: "percentage",
                                                                                                    checked: e.target.checked
                                                                                                })
                                                                                            }
                                                                                            disabled={isView || isSkeletonView}
                                                                                        />
                                                                                        <span className="checkmark" />
                                                                                    </label>
                                                                                </div>

                                                                                <div className="check-box-area">
                                                                                    <label
                                                                                        className={`container-check ${
                                                                                            isView || isSkeletonView ? "cursor-diabled" : ""
                                                                                        } green-check`}
                                                                                    >
                                                                                        Show Amount
                                                                                        <input
                                                                                            type="checkbox"
                                                                                            checked={!!chartConfigData?.show_columns?.Actual?.value}
                                                                                            onChange={e =>
                                                                                                handleChartConfigData("showColumns", null, {
                                                                                                    column: "Actual",
                                                                                                    field: "value",
                                                                                                    checked: e.target.checked
                                                                                                })
                                                                                            }
                                                                                            disabled={isView || isSkeletonView}
                                                                                        />
                                                                                        <span className="checkmark" />
                                                                                    </label>
                                                                                </div>
                                                                            </div>
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            </div>
                                                        </>
                                                    ) : null}
                                                </div>
                                            </div>
                                        </>
                                    ) : configData?.chartKey === "energy_band" ? (
                                        <div className="radio-area not-draggable">
                                            <div className="heading">
                                                <h3>Select Charts</h3>
                                            </div>
                                            <div className="radio-sec">
                                                <div className="button-group d-inline-block">
                                                    {existingKeys.map(key => (
                                                        <div className="button mb-2" key={key}>
                                                            <label
                                                                className={`container cursor-hand ${
                                                                    isView || isSkeletonView ? "cursor-disabled" : ""
                                                                }`}
                                                            >
                                                                <input
                                                                    type="checkbox"
                                                                    checked={energyChartType.includes(key)}
                                                                    onChange={e => {
                                                                        if (isView || isSkeletonView) return;
                                                                        const checked = e.currentTarget.checked;
                                                                        setEnergyChartType(prev =>
                                                                            checked ? [...prev, key] : prev.filter(k => k !== key)
                                                                        );
                                                                    }}
                                                                />
                                                                {chartLabels[key]}
                                                            </label>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        </div>
                                    ) : (
                                        <>
                                            <div className="radio-area not-draggable">
                                                <div className="heading">
                                                    <h3>Please Select Breakdown Type.</h3>
                                                </div>
                                                <div className="radio-sec">
                                                    <div className="button-group">
                                                        <div className="button">
                                                            <label
                                                                className={`container cursor-hand ${isView || isSkeletonView ? "cursor-diabled" : ""}`}
                                                            >
                                                                <input
                                                                    onChange={() => !isView && !isSkeletonView && setSortType("building")}
                                                                    checked={sortType === "building"}
                                                                    type="radio"
                                                                />
                                                                Building
                                                            </label>
                                                        </div>
                                                        <div className="button">
                                                            <label
                                                                className={`container cursor-hand ${isView || isSkeletonView ? "cursor-diabled" : ""}`}
                                                            >
                                                                <input
                                                                    onChange={() => !isView && !isSkeletonView && setSortType("addition")}
                                                                    checked={sortType === "addition"}
                                                                    type="radio"
                                                                />
                                                                Addition
                                                            </label>
                                                        </div>
                                                        <div className="button">
                                                            <label
                                                                className={`container cursor-hand ${isView || isSkeletonView ? "cursor-diabled" : ""}`}
                                                            >
                                                                <input
                                                                    onChange={() => !isView && !isSkeletonView && setSortType("responsibility")}
                                                                    checked={sortType === "responsibility"}
                                                                    type="radio"
                                                                />
                                                                Responsibility
                                                            </label>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                            {isSortedRecommendation && (
                                                <button
                                                    type="button"
                                                    className="btn btn-download outdated smart-chart-export-settings-btn"
                                                    onClick={() => setShowViewModalExport({ show: true, type: "sortedRecommendation" })}
                                                >
                                                    Advanced Export Settings <img src={RightArrowIcon} className="ml-1" alt="" />
                                                </button>
                                            )}
                                        </>
                                    )}
                                </DragDropContext>
                                <div className="btn-sec not-draggable">
                                    <div className="text-right btnOtr edit-cmn-btn mt-4">
                                        {Object.values(error)?.some(value => value === true) && (
                                            <span className="errorMessage">
                                                {`* Required field${
                                                    Object.values(error).filter(value => value === true).length > 1 ? "s are" : " is"
                                                } missing`}
                                            </span>
                                        )}
                                        <button
                                            type="button"
                                            className="btn btn-secondary btnClr col-md-2 mr-3"
                                            data-dismiss="modal"
                                            onClick={() => handleClickCancel()}
                                        >
                                            Cancel
                                        </button>
                                        {!isView && !isSkeletonView ? (
                                            <button type="button" className="btn btn-primary btnRgion col-md-2" onClick={() => handleClickSave()}>
                                                Save
                                            </button>
                                        ) : null}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </OuterDraggable>
                </div>
            </div>
            {exportSettingsModal()}
        </>
    );
};

const styles = reactCSS({
    default: {
        color: {
            width: "40px",
            height: "15px",
            borderRadius: "3px",
            zoom: "107%"
        },
        popover: {
            position: "absolute",
            zIndex: "3",
            left: "111px",
            top: "-76px"
        },
        cover: {
            position: "fixed",
            top: "0px",
            right: "0px",
            bottom: "0px",
            left: "0px"
        },
        swatch: {
            padding: "6px",
            background: "#ffffff",
            borderRadius: "2px",
            cursor: "pointer",
            display: "inline-block",
            boxShadow: "0 0 0 1px rgba(0,0,0,.2)"
        }
    }
});
const presetColors = ["#95cd50", "#ffe242", "#ffa105", "#ff0305", "#0018A8", "#800080", "#3E8EDE", "#417505"];
export default ChartItemConfigModal;
